import { ColDef, GridOptions, ICellRendererParams } from "ag-grid-community";
import { useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import {
  ExperienceActionDto,
  ExperienceListDto,
  ScheduleDto,
  TabEnumFe,
} from "@intelligems/ig-types";
import React from "react";
import { CustomCellRendererProps } from "ag-grid-react";
import { Link } from "react-router-dom";

import {
  IgChip,
  IgTooltip,
  IgTypography,
  IgTypographyTooltip,
} from "@intelligems/ig-theme";
import { Info } from "lucide-react";
import { officialCategoryName, sortByStatus } from "@intelligems/ig-utils";
import { ExperienceModuleGridContext } from "@src/domains/experienceTablePages/types";
import { useDispatch } from "@src/store";
import { useDialogs } from "@src/hooks/dialog";
import { EXPERIMENT_ROUTES, PERSONALIZATION_ROUTES } from "@src/routes/routes";
import {
  isRolloutEnabled,
  onPriceTestAction,
} from "@src/features/experiments/utils/experienceActionUtils";
import { updateDashboardFilter } from "@src/store/slices/dashboardFilters";
import { mpExperienceGenericAction } from "@src/utils/mixpanel";
import { AiTestNameSparkle } from "@src/components/Dialogs/AiTestNameSparkle";
import { getDefaultHiddenEndTime } from "@src/store/helpers";
import { getExperienceModificationFilterLabels } from "@src/domains/experienceTablePages/experienceModuleFilters";
import { getDaysFromHistory } from "@intelligems/ig-analytics";
import { MoreMenuCell } from "@src/features/experiments/pages/home/components/experiencesList/renderers/MoreMenuCell";
import { MoreMenuPersonalization } from "@src/features/personalizations/components/MoreMenuPersonalization";
import { isUsedInExperiment } from "@src/features/personalizations/utils";
import { ExperimentActionButtons } from "@src/features/sharedExperience/components/ExperimentActionButtons";
import { SharedActionColumn } from "@src/features/sharedExperience/components/sharedTableRenderers";
import { FoundExperimentDetails } from "@src/types/experience";
import { getRuntimeDisplayLabel } from "@src/domains/experienceTablePages/experienceRuntime";
import { getExperienceTimelineLabel } from "@src/domains/experienceTablePages/experienceTimeline";

export const GRID_OPTIONS: GridOptions<ExperienceListDto> = {
  getRowId: (params) => params.data.id,
};

export const SharedTitleColumn = (
  props: ICellRendererParams<ExperienceListDto> & {
    storeCurrencyCode: string | undefined;
    renderLocation: string;
  }
) => {
  const {
    data = {} as ExperienceListDto,
    storeCurrencyCode,
    renderLocation,
  } = props;
  const theme = useTheme();
  const dispatch = useDispatch();
  const { openDialog } = useDialogs();

  if (!data || !data.id) {
    console.warn("SharedTitleColumn: Received invalid data", { data });
    return null;
  }

  const experienceType =
    data.category === "personalization" ? "" : `&type=${data.type}`;
  const navigateLink = `${data.category === "personalization" ? PERSONALIZATION_ROUTES.Personalization : EXPERIMENT_ROUTES.Experiment}/${data.id}?action=edit${experienceType}&tab=${
    data.status === "pending" ? TabEnumFe.MODS : TabEnumFe.ANALYTICS
  }`;
  const isEdit = data.status === "pending" || data.status === "paused";
  const state = isEdit ? { id: data.id } : { name: "overview" };

  const renderRolloutOption =
    data.category === "experiment" &&
    (data.status === "started" || data.status === "paused") &&
    isRolloutEnabled(data) &&
    data.variations.some((variation) => variation.percentage === 100);

  return (
    <Stack>
      <Grid
        container
        direction={"row"}
        alignItems={"center"}
        justifyContent={"flex-start"}
        columnSpacing={1}
      >
        <Grid size="grow">
          <Link
            style={{
              textDecoration: "none",
              fontWeight: 400,
            }}
            to={navigateLink}
            state={state}
            onClick={() => {
              if (!isEdit) {
                dispatch(
                  updateDashboardFilter({
                    key: "experienceId",
                    value: undefined,
                  })
                );
              }

              mpExperienceGenericAction(data, "viewed", renderLocation);
            }}
          >
            <Stack>
              <IgTypographyTooltip
                variant={"body3"}
                textMaxWidth={"380px"}
                text={officialCategoryName(data.category, false)}
                textSx={{
                  fontWeight: 400,
                  color: theme.palette.text.placeholder,
                  textTransform: "uppercase",
                }}
              />

              <IgTypographyTooltip
                textMaxWidth={"380px"}
                text={data.name}
                textSx={{ fontWeight: 500 }}
                variant={"body1"}
              />
            </Stack>
          </Link>
        </Grid>
        <Grid>
          <AiTestNameSparkle
            experienceId={data.id}
            experienceName={data.name}
          />
        </Grid>
        {renderRolloutOption ? (
          <Grid>
            <IgTooltip
              title={
                <IgTypography sx={{ display: "block" }}>
                  <IgTypography fontWeight={600}>
                    Trying to personalize?
                  </IgTypography>
                  <div>&nbsp;</div>
                  This test is sending 100% of traffic to one group. It’s better
                  to roll it out as a Personalization. Personalizations support
                  the same changes, targeting, and analytics without the need
                  for test groups.
                  <div>&nbsp;</div>
                  <IgTypography
                    onClick={() => {
                      if (data.testTypes.hasTestPricing) {
                        onPriceTestAction(
                          data,
                          "end",
                          storeCurrencyCode ?? "USD",
                          dispatch
                        );
                      } else {
                        dispatch(
                          updateDashboardFilter({
                            key: "hiddenEndTime",
                            value: getDefaultHiddenEndTime(data),
                          })
                        );
                        openDialog("rollouts");
                      }
                    }}
                    sx={{
                      textDecoration: "underline",
                      "&:hover": {
                        cursor: "pointer",
                      },
                    }}
                  >
                    Roll out to a Personalization
                  </IgTypography>
                </IgTypography>
              }
            >
              <Info
                size={16}
                color={theme.igIconColors.warning}
                style={{
                  cursor: "pointer",
                  marginTop: "14px",
                }}
              />
            </IgTooltip>
          </Grid>
        ) : null}
      </Grid>
    </Stack>
  );
};

export const VisitorsCell = (
  params: CustomCellRendererProps<ExperienceListDto>
) => {
  const theme = useTheme();
  const experience = params.data;
  const context = params.context as ExperienceModuleGridContext;

  if (!experience || experience.status === "pending") {
    return (
      <IgTypography color={theme.palette.text.secondary}>-------</IgTypography>
    );
  }

  if (context.summaryMetricsIsLoading) {
    return (
      <IgTypography color={theme.palette.text.secondary}>...</IgTypography>
    );
  }

  return (
    <IgTypography>
      {context.summaryMetricsData?.[experience.id]?.visitors ?? "0"}
    </IgTypography>
  );
};

export const RuntimeLabel = ({
  experienceActions,
  hasExpEverStarted,
}: {
  experienceActions: ExperienceActionDto[];
  hasExpEverStarted: boolean;
}) => {
  const theme = useTheme();

  if (!hasExpEverStarted) {
    return <IgTypography color={theme.palette.text.secondary}>-</IgTypography>;
  }

  const runtimeDays = Math.ceil(getDaysFromHistory(experienceActions));
  const label = getRuntimeDisplayLabel(runtimeDays);

  return (
    <IgTypography color={theme.palette.text.secondary} variant={"body1"}>
      {label}
    </IgTypography>
  );
};

export const TimelineCell = (
  params: CustomCellRendererProps<ExperienceListDto>
) => {
  const theme = useTheme();
  const experience = params.data;
  const context = params.context as ExperienceModuleGridContext;

  if (!experience) {
    return null;
  }

  const label = getExperienceTimelineLabel(
    experience,
    context.organization?.timezone
  );

  if (!label) {
    return (
      <IgTypography variant={"body2"} color={theme.palette.text.secondary}>
        -------
      </IgTypography>
    );
  }

  return (
    <IgTypography variant={"body2"} color={theme.palette.text.secondary}>
      {label}
    </IgTypography>
  );
};

export const ModificationsCell = (
  params: CustomCellRendererProps<ExperienceListDto>
) => {
  const theme = useTheme();
  const experience = params.data;

  if (!experience) {
    return null;
  }

  const labels = getExperienceModificationFilterLabels(experience);

  if (labels.length === 0) {
    return (
      <IgTypography color={theme.palette.text.secondary}>-------</IgTypography>
    );
  }

  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {labels.map((label) => (
        <IgChip key={label} size="small" variant="outlined" label={label} />
      ))}
    </Stack>
  );
};

export const ActionsCell = (
  params: CustomCellRendererProps<ExperienceListDto>
) => {
  const context = params.context as ExperienceModuleGridContext;
  const experience = params.data;

  if (!experience || !context.organization) {
    return null;
  }

  const setCurrentExperience = () => {
    context.setCurrentExperience(experience);
  };

  if (experience.category === "experiment") {
    return (
      <Stack component="span" onClickCapture={setCurrentExperience}>
        <MoreMenuCell {...params} />
      </Stack>
    );
  }

  return (
    <Stack component="span" onClickCapture={setCurrentExperience}>
      <MoreMenuPersonalization
        isUsedInTest={isUsedInExperiment(
          context.experiencesList,
          experience.id
        )}
        experience={experience}
        variations={experience.variations}
        organization={context.organization}
        renderLocation="overview"
        schedules={experience.schedules ?? []}
        isSchedulesLoading={false}
        scriptStatus={context.scriptStatus}
      />
    </Stack>
  );
};

export const ExperimentActionButtonsCell = (
  params: CustomCellRendererProps<ExperienceListDto> & {
    hasPriceModAccess: boolean;
    hasOfferModAccess: boolean;
    hasCheckoutBlockModAccess: boolean;
    inactiveSubscriptionGate: boolean;
    isUnderOfferLimit: boolean;
    isAdmin: boolean;
    isSchedulesLoading: boolean;
    schedules: ScheduleDto[];
    onActionClick: (personalization: ExperienceListDto) => void;
    getIsUsedInTest: (experienceId: string) => FoundExperimentDetails;
  }
) => {
  const context = params.context as ExperienceModuleGridContext;

  const experience = params.data;

  if (!experience || !context.organization) {
    return null;
  }

  if (experience.category === "personalization") {
    return <SharedActionColumn {...params} />;
  }

  return (
    <Stack
      component="span"
      onClickCapture={() => context.setCurrentExperience(experience)}
    >
      <ExperimentActionButtons
        experiencesList={context.experiencesList}
        organization={context.organization}
        renderLocation="overview"
        onlyStart={experience.status === "pending"}
        rowExperiment={experience}
        currentExperiment={context.currentExperience}
        onExperimentEnd={context.onExperimentEnd}
        onExperimentPause={context.onExperimentPause}
        onExperimentStartWrapper={context.onExperimentStartWrapper}
        hasSchedule={false}
        actionMode="edit"
      />
    </Stack>
  );
};

export const DEFAULT_COL_DEF: ColDef<ExperienceListDto> = {
  flex: 1,
  minWidth: 120,
  resizable: false,
  sortable: false,
  menuTabs: [],
  suppressMovable: true,
  suppressHeaderMenuButton: true,
  cellStyle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  },
};

export const sortModuleRows = (experiences: ExperienceListDto[]) =>
  sortByStatus([...experiences]).sort((a, b) => {
    if (!a || !b || Boolean(a.archivedAtTs) === Boolean(b.archivedAtTs)) {
      return 0;
    }
    return a.archivedAtTs ? 1 : -1;
  });
